import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getProfile } from "../../api/profileAPI";
import { getPublishedPosts } from "../../api/blogAPI";

const BASE = process.env.REACT_APP_EXPRESS_API_BASE_URL || "http://localhost:3001";

const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BASE}${url}`;
};

const ProfileSidebar = () => {
  const [profile, setProfile] = useState(null);
  const [latestPost, setLatestPost] = useState(null);

  useEffect(() => {
    getProfile()
      .then((d) => setProfile(d.profile ?? d))
      .catch(() => {});
    getPublishedPosts()
      .then((posts) => {
        if (posts.length > 0) setLatestPost(posts[0]);
      })
      .catch(() => {});
  }, []);

  const name = profile?.full_name || "Affan Haidar Atstsabit";
  const headline = profile?.headline || "NOC Engineer · Cybersecurity Enthusiast";
  const photo = resolveImg(profile?.photo_url);
  const email = profile?.social_email || "";
  const birthday = "October 07, 2000";
  const location = "South Tangerang";

  return (
    <aside className="bg-[#111116] border border-zinc-800 rounded-2xl overflow-hidden sticky top-4 transition-all duration-300">
      {/* Top section - photo + name */}
      <div className="relative pb-6">
        {/* Blue glow bar at top */}
        <div className="h-1 w-full bg-gradient-to-r from-blue-600 via-blue-400 to-cyan-400" />

        <div className="pt-8 pb-2 px-6 flex flex-col items-center text-center">
          {/* Avatar */}
          <div className="relative mb-5">
            <div className="w-28 h-36 rounded-xl overflow-hidden border-2 border-zinc-700 shadow-lg shadow-blue-900/20">
              {photo ? (
                <img
                  src={photo}
                  alt={name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-900/40 to-zinc-900 flex items-center justify-center text-4xl font-black text-blue-400 select-none">
                  {name.charAt(0)}
                </div>
              )}
            </div>
            {/* Online dot */}
            <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-[#111116] rounded-full shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
          </div>

          <h1 className="text-white font-black text-lg leading-tight tracking-tight">{name}</h1>
          <p className="text-blue-400 text-xs font-semibold mt-1 tracking-wide">{headline}</p>
        </div>
      </div>

      {/* Divider */}
      <div className="mx-6 border-t border-zinc-800" />

      {/* Info items */}
      <div className="px-6 py-5 space-y-4">
        {[
          {
            icon: (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            ),
            label: "EMAIL",
            value: email,
          },
          {
            icon: (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            ),
            label: "BIRTHDAY",
            value: birthday,
          },
          {
            icon: (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            ),
            label: "LOCATION",
            value: location,
          },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-3 group">
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-600/10 border border-blue-600/20 flex items-center justify-center text-blue-400 transition-colors group-hover:bg-blue-600/20">
              {item.icon}
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">{item.label}</p>
              <p className="text-white text-xs font-medium truncate">{item.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Divider */}
      <div className="mx-6 border-t border-zinc-800" />

      {/* Social links */}
      <div className="px-6 py-5 flex justify-center gap-4">
        {[
          {
            href: profile?.show_linkedin ? profile?.social_linkedin : null,
            label: "LinkedIn",
            icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>,
          },
          {
            href: profile?.show_instagram ? profile?.social_instagram : null,
            label: "Instagram",
            icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>,
          },
          {
            href: profile?.show_github ? profile?.social_github : null,
            label: "GitHub",
            icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" /></svg>,
          },
        ].filter((s) => s.href).map((s, i) => (
          <a
            key={i}
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            title={s.label}
            className="w-10 h-10 rounded-xl bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:text-blue-400 hover:border-blue-500/50 hover:bg-blue-600/10 transition-all duration-200"
          >
            {s.icon}
          </a>
        ))}
      </div>

      {/* Latest Blog Post */}
      {latestPost && (
        <>
          <div className="mx-6 border-t border-zinc-800" />
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Latest Post</span>
              <Link
                to="/blog"
                className="text-[10px] text-blue-400 hover:text-blue-300 font-semibold transition-colors"
              >
                All posts →
              </Link>
            </div>
            <Link
              to={`/blog/${latestPost.slug}`}
              className="group block bg-zinc-800/60 hover:bg-zinc-800 border border-zinc-700 hover:border-zinc-600 rounded-xl p-3 transition-all duration-200"
            >
              <p className="text-white text-xs font-semibold leading-snug group-hover:text-blue-300 transition-colors line-clamp-2">
                {latestPost.title}
              </p>
              {latestPost.excerpt && (
                <p className="text-zinc-500 text-[10px] mt-1 leading-relaxed line-clamp-2">{latestPost.excerpt}</p>
              )}
              <div className="mt-2 flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-blue-500" />
                <span className="text-[10px] text-zinc-600">
                  {latestPost.published_at
                    ? new Date(latestPost.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                    : ""}
                </span>
              </div>
            </Link>
          </div>
        </>
      )}

      {/* Contact Buttons */}
      <div className="px-6 pb-4 flex gap-2">
        <a
          href={`https://wa.me/628997779254?text=${encodeURIComponent("Halo Affan, saya melihat portfolio kamu dan ingin terhubung.")}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 flex-1 py-2.5 bg-green-600 hover:bg-green-500 active:scale-95 text-white font-bold text-xs rounded-xl transition-all duration-200"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.126.553 4.122 1.523 5.854L.057 23.882a.5.5 0 00.611.641l6.193-1.624A11.944 11.944 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.907 0-3.693-.502-5.241-1.381l-.374-.217-3.876 1.017 1.035-3.78-.237-.389A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
          </svg>
          WhatsApp
        </a>
        <a
          href={`mailto:affanhaidar232@gmail.com?subject=${encodeURIComponent("Hi Affan — from your portfolio")}&body=${encodeURIComponent("Halo Affan,\n\nSaya melihat portfolio kamu dan ingin terhubung.\n\n")}`}
          className="flex items-center justify-center gap-2 flex-1 py-2.5 bg-zinc-700 hover:bg-zinc-600 active:scale-95 text-white font-bold text-xs rounded-xl transition-all duration-200"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Email
        </a>
      </div>

      {/* CV Button */}
      {(profile?.cv_pdf_url || profile?.cv_docx_url) && (
        <div className="px-6 pb-6 flex gap-2">
          {profile?.cv_pdf_url && (
            <a
              href={resolveImg(profile.cv_pdf_url)}
              download
              className="flex items-center justify-center gap-2 flex-1 py-3 bg-blue-600 hover:bg-blue-500 active:scale-95 text-white font-bold text-sm rounded-xl transition-all duration-200 shadow-lg shadow-blue-900/30"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              PDF
            </a>
          )}
          {profile?.cv_docx_url && (
            <a
              href={resolveImg(profile.cv_docx_url)}
              download
              className="flex items-center justify-center gap-2 flex-1 py-3 bg-zinc-700 hover:bg-zinc-600 active:scale-95 text-white font-bold text-sm rounded-xl transition-all duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              DOCX
            </a>
          )}
        </div>
      )}
    </aside>
  );
};

export default ProfileSidebar;
